import { createFileRoute } from "@tanstack/react-router";
import { HomePage } from "@/components/home-page";
import { getReaderPickTitlesFn } from "@/server/reader-picks";
import {
  pageTitle,
  siteTitle,
  siteTagline,
  siteSocialImage,
} from "@/config/site";
import { localeUrl } from "@/lib/seo";
import { homeJsonLd, jsonLdScripts } from "@/lib/jsonld";

// See ($locale)/index.tsx: the homepage deliberately has no page-specific
// description/og:description override — __root.tsx's site-wide meta supplies
// it, matching tianpan-v3-web's homepage.
//
// zh tagline: the homepage <title> renders the tagline translated (a
// Docusaurus-parity behavior, see w1/m4/t005). `head()` runs outside the
// React tree (no useTranslation()), so this is a small, locale-scoped
// constant rather than pulling the full i18n message-loading machinery into
// route head()s; only this one page needs it today. Keep in sync with
// `site.tagline` in i18n/zh/code.json.
const ZH_TAGLINE = "为 AI 智能体构建信任层";

export const Route = createFileRoute("/$locale/")({
  // Pick titles come from front matter (see src/data/reader-picks.ts); at
  // build time this runs once per prerendered locale.
  loader: ({ params }) =>
    getReaderPickTitlesFn({ data: { locale: params.locale } }),
  head: ({ params }) => {
    // Trailing slash on this one exact URL — localeUrlFor() already appends
    // it for the home logical path, see src/lib/seo.ts.
    const canonicalUrl = localeUrl(params.locale, "/");
    const tagline = params.locale === "zh" ? ZH_TAGLINE : siteTagline;
    const title = pageTitle(`${siteTitle} - ${tagline}`);
    return {
      meta: [
        { title },
        { property: "og:title", content: title },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: siteSocialImage },
      ],
      scripts: jsonLdScripts(...homeJsonLd(canonicalUrl)),
      links: [{ rel: "canonical", href: canonicalUrl }],
    };
  },
  component: HomeRoute,
});

function HomeRoute() {
  return <HomePage pickTitles={Route.useLoaderData()} />;
}
